import type { ReactNode } from 'react';
import { usePermissions } from './usePermissions';
import type { OrOfAnds, PermissionString } from '@eis/core';

export interface RequirePermissionProps {
  /** At least one of these. */
  readonly anyOf?: readonly PermissionString[];
  /** All of these. */
  readonly allOf?: readonly PermissionString[];
  /** OR-of-AND-groups, for the cases the two simple forms cannot express. */
  readonly groups?: OrOfAnds;
  /** Rendered instead when the check fails. Defaults to rendering nothing. */
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

/**
 * The single sanctioned gate for conditional UI (Addendum A.2, enforcement layer 2): buttons,
 * menu entries, form sections, grid actions.
 *
 * Rendering nothing by default is deliberate — a disabled-looking control the user can never
 * enable is worse UX than an absent one, and matches how the nav filters itself.
 *
 * These checks are UX affordances. **The API is the security boundary**; a hidden button is not
 * an access control, and module code must never treat it as one.
 */
export function RequirePermission({
  anyOf,
  allOf,
  groups,
  fallback = null,
  children,
}: RequirePermissionProps): ReactNode {
  const { canAny, canAll, canGroups } = usePermissions();

  const checks: boolean[] = [];
  if (anyOf !== undefined) checks.push(canAny(anyOf));
  if (allOf !== undefined) checks.push(canAll(allOf));
  if (groups !== undefined) checks.push(canGroups(groups));

  // No declaration at all is a mistake, not "allow": a gate that gates nothing is a silent hole.
  if (checks.length === 0) {
    throw new Error('<RequirePermission> needs one of anyOf, allOf or groups.');
  }

  return checks.every(Boolean) ? children : fallback;
}
